import { BlueprintVertex } from '../vertex.js';
import * as constants from '../constants.js';
import { fromTiptap } from '../tiptap-wysiwyg.js';

/**
 * @typedef TipTapContent
 * WYSIWYG content that needs parsed
 */
export class BlueprintText extends BlueprintVertex {
  /**
   * @param {fabric.Canvas} canvas - the fabric canvas
   * @param {Object} config - Configuration for the text vertex
   * @param {string|TipTapContent} config.content - the content of the text to be displayed, or a tiptap content object
   * @param {Object=} config.fabricOptions - shape options to be passed into fabric
   * @param {BlueprintShape} [config.parentShape] - the shape this will be associated with. Both exist as independent vertices linked manually
   * @param {fabric.Object} [config.fabricObject] - a fabric IText instance
   * @returns {BlueprintText}
   */
  constructor(
    canvas,
    {
      content = constants.DEFAULT_BLUEPRINT_TEXT,
      fabricOptions = {},
      // parentShape,
      fabricObject,
    },
  ) {
    super(canvas, { vertexType: constants.CELL_TYPES.text });

    /** @todo: if content is a tiptap object, parse it? */
    if (fabricObject && fabricObject.type === 'i-text') {
      this.ref = fabricObject;

      // disable double-click editing
      this.ref.set('editable', false);

      // eslint-disable-next-line no-constructor-return
      return this;
    }

    /** @type fabric.IText */
    this.ref = new fabric.IText(content, {
      fontFamily: '"Open Sans", sans-serif',
      // root 1rem
      fontSize: 14,
      editable: false,
      textAlign: 'center',
      originX: 'center',
      originY: 'center',
      ...fabricOptions,
    });

    this.ref.setControlsVisibility({
      mt: false,
      mb: false,
      mr: false,
      ml: false,
    });
  }

  get color() {
    return this.ref.fill;
  }

  set color(value) {
    this.ref.set('fill', value);
    this.render();
  }

  get styles() {
    return this.ref.styles;
  }

  set styles(styles) {
    this.ref.set('styles', styles);
    this.render();
  }

  get text() {
    return this.ref.text;
  }

  set text(value) {
    this.ref.set('text', value);
    this.render();
  }

  get clonableProps() {
    const { angle, scaleX, scaleY, styles, fill } = this.ref;

    return { angle, scaleX, scaleY, styles, fill };
  }

  updateFromTiptap = content => {
    this.ref.set(fromTiptap(content));
    this.render();
  };

  /** Clone this text by returning a new instance */
  clone() {
    const { ref, canvas, text: content, clonableProps: fabricOptions } = this;
    const newText = new BlueprintText(canvas, { content, fabricOptions });

    newText.setCenterPoint({ x: ref.left + 20, y: ref.top + 20 });

    return newText;
  }
}
